/**
 * Page /service-technique/arretes/:id — Détail d'un arrêté.
 *
 * Layout 2 colonnes (desktop) : preview PDF (gauche) + workflow signature 4 étapes (droite).
 * Workflow visuel : Généré ✓ → Relecture juridique → Signature maire → Publication
 *
 * Refonte R6.2 (mai 2026) :
 *   - Topbar glass cohérent + sub-nav avec retour
 *   - Card glass principale 2 colonnes (preview PDF iframe + sidebar workflow)
 *   - WorkflowCard refondue avec dots tech sur ring bg + connecteurs success
 *   - Actions sidebar en Card glass séparée
 *   - Édition inline en formulaire glass
 *   - ArreteNouveauPage : page création avec pré-remplissage depuis signalementIds
 *
 * Logique métier 100% préservée :
 *   - api.serviceTechnique.arretes.get/transition/delete/update/create/pdfUrl/draftFromSignalements
 *   - Permissions canEdit, canSign, canSendToReview, canValidateReview, canPublish, canDelete
 *   - STATUTS_ORDER timeline + stepState (done/current/pending)
 *   - ArreteEditForm avec parsing articles "Article N — texte"
 *   - ArreteNouveauPage avec signalementIds query string + draft pré-rempli
 */

import { ActeWorkflowFrise, type StatutFrise } from '@/components/ActeWorkflowFrise';
import { ChampsModele, appliquerChamps, type ChampModele } from '@/components/ChampsModele';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { RelectureValidationCard } from '@/components/service-technique/RelectureValidationCard';
import { EnvoiArreteSigneModal } from '@/pages/service-technique/EnvoiArreteSigneModal';
import { VoiesMotifsEditor, nettoyerVoiesMotifs, type Voie } from '@/components/service-technique/VoiesMotifsEditor';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';                                 // R13 — remplace window.confirm/prompt
import { Button, Badge, Card, Alert, Field, Input, LogoMark, SectionEyebrow } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import {
  Landmark, FileSpreadsheet, ArrowLeft, Edit, Download, Send,
  CheckCircle2, Clock, Trash2, Link as LinkIcon, X, AlertCircle,
  FileSignature, Mail,
} from 'lucide-react';

type Arrete = Awaited<ReturnType<typeof api.serviceTechnique.arretes.get>>;

const STATUTS_ORDER = ['genere', 'relecture_juridique', 'a_signer', 'signe', 'publie'] as const;
const STATUTS_LABEL: Record<string, string> = {
  genere:              'Généré',
  relecture_juridique: 'Relecture juridique',
  a_signer:            'À signer (maire)',
  signe:               'Signé',
  publie:              'Publié',
  annule:              'Annulé',
};

// Topbar style partagé
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

// =============================================================================
// PAGE DÉTAIL / ÉDITION
// =============================================================================

export function ArreteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const { confirm, prompt, dialogElement } = useConfirmDialog();   // R13
  const [arrete, setArrete] = useState<Arrete | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  // R13.3 — PDF chargé en Blob via fetch authentifié (l'iframe ne peut pas passer le JWT)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // ⚠️ CE HOOK ÉTAIT DÉCLARÉ PLUS BAS, APRÈS LES `return` de chargement et
  // d'erreur. React comptait donc un nombre de hooks différent selon que
  // l'arrêté était chargé ou non — d'où l'écran noir et l'erreur #310 à
  // l'ouverture de toute fiche (remonté par la mairie le 28/07).
  // Règle : tous les hooks avant le premier `return`, sans exception.
  const [envoiOuvert, setEnvoiOuvert] = useState(false); // M17
  // M19 — pièces jointes de l'arrêté.
  const [pieces, setPieces] = useState<{ id: string; nom: string; mime: string; taille: number; ajouteParNom: string | null; createdAt: string }[]>([]);
  const [pieceEnvoi, setPieceEnvoi] = useState(false);
  const [pieceErreur, setPieceErreur] = useState<string | null>(null);

  const chargerPieces = useCallback(async (arreteId: string) => {
    try {
      const r = await api.serviceTechnique.arretes.pieces.list(arreteId);
      setPieces(r.pieces);
    } catch { /* la fiche vit sans ses pièces (stockage absent en dev) */ }
  }, []);

  const ajouterPiece = async (f: File) => {
    if (!id) return;
    setPieceEnvoi(true); setPieceErreur(null);
    try {
      await api.serviceTechnique.arretes.pieces.add(id, f);
      await chargerPieces(id);
    } catch (e) {
      setPieceErreur(e instanceof Error ? e.message : 'Envoi impossible');
    } finally { setPieceEnvoi(false); }
  };

  const ouvrirPiece = async (pieceId: string) => {
    if (!id) return;
    try {
      const { url } = await api.serviceTechnique.arretes.pieces.lien(id, pieceId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setPieceErreur(e instanceof Error ? e.message : 'Téléchargement impossible');
    }
  };

  const retirerPiece = async (pieceId: string) => {
    if (!id) return;
    try {
      await api.serviceTechnique.arretes.pieces.remove(id, pieceId);
      setPieces((p) => p.filter((x) => x.id !== pieceId));
    } catch (e) {
      setPieceErreur(e instanceof Error ? e.message : 'Suppression impossible');
    }
  };

  async function reload() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.serviceTechnique.arretes.get(id);
      setArrete(data);
      void chargerPieces(id); // M19 — en parallèle, sans bloquer la fiche
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  // R13.3 — Chargement PDF en blob authentifié à chaque changement d'arrêté
  useEffect(() => {
    if (!arrete?.id) return;
    let cancelled = false;
    let currentUrl: string | null = null;

    setPdfLoading(true);
    setPdfError(null);

    api.serviceTechnique.arretes
      .pdfBlob(arrete.id)
      .then((blob) => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(currentUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof ApiException
          ? `${err.status}: ${err.payload.error}`
          : 'Échec chargement PDF';
        setPdfError(msg);
        console.error('[PDF] fetch blob échoué:', err);
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });

    // Cleanup : révoque l'ancien blob URL et signale qu'on est annulé
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setPdfBlobUrl(null);
    };
  }, [arrete?.id, arrete?.updatedAt]);  // recharge si arrêté re-fetché (transitions)

  // R13.1 — Topbar inline (style cohérent avec ArretesListPage / SignalementsListPage)
  const Topbar = (
    <AppHeader module="Service technique" accent="tech" />
  );

  // R13.1 — Sub-nav (la pièce manquante d'origine)
  const SubNav = (
    <nav
      aria-label="Navigation Service technique"
      className="border-b border-border backdrop-blur-glass px-4 py-2"
      style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
    >
      <div className="mx-auto max-w-6xl">
        <ServiceTechniqueNav
          active="arretes"
          right={
            <Button variant="ghost" size="sm" onClick={() => navigate('/service-technique/arretes')}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Liste des arrêtés</span>
            </Button>
          }
        />
      </div>
    </nav>
  );

  if (loading) {
    return (
      <div className="min-h-screen text-ink">
        {Topbar}
        {SubNav}
        <main className="mx-auto max-w-6xl px-4 py-12 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" />
          <p className="mt-4 text-sm text-ink-muted">Chargement de l'arrêté…</p>
        </main>
        {dialogElement}
      </div>
    );
  }

  if (error || !arrete) {
    return (
      <div className="min-h-screen text-ink">
        {Topbar}
        {SubNav}
        <main className="mx-auto max-w-3xl px-4 py-8 animate-fade-in space-y-4">
          <Alert variant="error" icon={AlertCircle}>{error ?? 'Arrêté introuvable'}</Alert>
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/service-technique/arretes')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à la liste
          </Button>
        </main>
        {dialogElement}
      </div>
    );
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const canEdit = arrete.statut === 'genere' && (isAdmin || user?.role === 'manager');
  const canSign = arrete.statut === 'a_signer' && isAdmin;
  const canSendToReview = arrete.statut === 'genere';
  const canValidateReview = arrete.statut === 'relecture_juridique';
  const canPublish = arrete.statut === 'signe';
  const canDelete = isAdmin && arrete.statut !== 'publie';

  async function doTransition(statut: string, commentaire?: string) {
    try {
      await api.serviceTechnique.arretes.transition(arrete!.id, { statut, commentaire });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur transition workflow');
    }
  }

  async function doDelete() {
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Annuler définitivement cet arrêté ?',
      description: 'L\'arrêté sera marqué comme annulé. Cette action est irréversible.',
      confirmLabel: 'Oui, annuler l\'arrêté',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.serviceTechnique.arretes.delete(arrete!.id);
      navigate('/service-technique/arretes');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur suppression');
    }
  }

  return (
    <div className="min-h-screen text-ink">
      {Topbar}
      {SubNav}

      {/* Meta : "Mis à jour ..." */}
      <div className="border-b border-border bg-surface/30 backdrop-blur-glass px-4 py-1.5 sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-end">
          <div className="text-xs text-ink-subtle">
            Mis à jour {new Date(arrete.updatedAt).toLocaleString('fr-FR')}
          </div>
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-5 animate-fade-in">
        {/* En-tête : icône + numéro + signalements source */}
        <div className="mb-4 flex items-center gap-3">
          <LogoMark icon={FileSignature} accent="tech" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">Arrêté municipal</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-lg font-semibold tracking-r-snug text-ink">
              {arrete.numero}
              {arrete.signalementsDetail && arrete.signalementsDetail.length > 0 && (
                <span className="ml-2 text-sm font-normal text-ink-muted">
                  · généré depuis fiche{arrete.signalementsDetail.length > 1 ? 's' : ''} {arrete.signalementsDetail.map((s) => s.numero).join(', ')}
                </span>
              )}
            </h1>
          </div>
        </div>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        {editMode ? (
          <ArreteEditForm
            arrete={arrete}
            onCancel={() => setEditMode(false)}
            onSaved={async () => { setEditMode(false); await reload(); }}
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
            {/* Colonne gauche : preview PDF */}
            <Card variant="glass" padded={false}>
              <div className="p-2">
                {pdfLoading && (
                  <div className="flex h-[700px] w-full items-center justify-center rounded-r-lg border border-border bg-surface/40 text-sm text-ink-muted">
                    <div className="text-center">
                      <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" />
                      <p className="mt-3">Génération du PDF…</p>
                    </div>
                  </div>
                )}
                {pdfError && !pdfLoading && (
                  <div className="flex h-[700px] w-full items-center justify-center rounded-r-lg border border-urgent/40 bg-urgent-tint/20 p-6 text-sm text-urgent-fg">
                    <div className="text-center">
                      <AlertCircle className="mx-auto h-8 w-8" aria-hidden="true" />
                      <p className="mt-3 font-medium">Aperçu PDF indisponible</p>
                      <p className="mt-1 text-xs text-ink-muted">{pdfError}</p>
                    </div>
                  </div>
                )}
                {pdfBlobUrl && !pdfLoading && !pdfError && (
                  <iframe
                    src={pdfBlobUrl}
                    title={`Aperçu PDF de l'arrêté ${arrete.numero}`}
                    className="h-[700px] w-full rounded-r-lg border border-border bg-white"
                  />
                )}
              </div>
            </Card>

            {/* Colonne droite : workflow + actions */}
            <aside aria-label="Workflow et actions" className="space-y-3">
              <WorkflowCard arrete={arrete} />

              {/* Liens signalements source */}
              {arrete.signalementsDetail && arrete.signalementsDetail.length > 0 && (
                <Card variant="glass" padded>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-tech-fg">
                    <LinkIcon className="h-3 w-3" aria-hidden="true" /> Signalements source
                  </div>
                  <div className="mt-2 space-y-1">
                    {arrete.signalementsDetail.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => navigate(`/service-technique/signalements/${s.id}`)}
                        className="block w-full rounded-r-md px-2 py-1.5 text-left text-xs text-ink transition-colors hover:bg-tech/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        <span className="font-mono font-medium">{s.numero}</span>
                        <span className="text-ink-muted"> · {s.sujet}</span>
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* Actions */}
              <Card variant="glass" padded>
                <SectionEyebrow as="div" className="mb-3">Actions</SectionEyebrow>
                <div className="space-y-2">
                  {canEdit && (
                    <Button type="button" variant="secondary" size="sm" className="w-full" onClick={() => setEditMode(true)}>
                      <Edit className="h-4 w-4" aria-hidden="true" /> Modifier
                    </Button>
                  )}

                  {/* R13.3 — Télécharge le blob déjà chargé en mémoire (avec JWT déjà transmis)
                       au lieu d'un href direct qui retournerait 401. */}
                  <a
                    href={pdfBlobUrl ?? '#'}
                    download={`arrete-${arrete.numero}.pdf`}
                    className="block"
                    aria-disabled={!pdfBlobUrl}
                    onClick={(e) => { if (!pdfBlobUrl) e.preventDefault(); }}
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full"
                      disabled={!pdfBlobUrl}
                      tabIndex={-1}
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      {pdfBlobUrl ? 'Télécharger PDF' : 'PDF en cours…'}
                    </Button>
                  </a>

                  {/* 28/09 : l'envoi en relecture et validation se fait dans la carte « Relecture et validation ». */}
                  {arrete.relectureEnvoyeeNom && (
                    <p className="text-xs text-ink-subtle">
                      Relecture demandée à {arrete.relectureEnvoyeeNom}
                      {arrete.relectureEnvoyeeAt
                        ? ` le ${new Intl.DateTimeFormat('fr-FR', {
                            day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris',
                          }).format(new Date(arrete.relectureEnvoyeeAt))}`
                        : ''}
                    </p>
                  )}

                  {canValidateReview && (
                    <Button
                      type="button" variant="primary" size="sm" className="w-full"
                      onClick={async () => {
                        // R13 — PromptDialog R2 au lieu de window.prompt
                        const c = await prompt({
                          title: 'Valider la relecture',
                          description: 'L’arrêté passera « À signer (maire) ». Ajoutez un commentaire si besoin (optionnel).',
                          placeholder: 'Ex : "Vérifier la base légale article 3, merci."',
                          multiline: true,
                          maxLength: 500,
                          confirmLabel: 'Valider',
                        });
                        if (c === null) return;  // annulé
                        void doTransition('a_signer', c || undefined);
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Valider la relecture
                    </Button>
                  )}

                  {canSign && (
                    <Button type="button" variant="primary" size="sm" className="w-full" onClick={async () => {
                      // R13 — ConfirmDialog R2 au lieu de window.confirm
                      const ok = await confirm({
                        title: 'Signer cet arrêté ?',
                        description: 'Votre signature électronique sera enregistrée et l\'arrêté passera en publication. Cette action est irréversible.',
                        confirmLabel: 'Signer définitivement',
                      });
                      if (ok) void doTransition('signe');
                    }}>
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Signer (maire)
                    </Button>
                  )}

                  {canPublish && (
                    <Button type="button" variant="success" size="sm" className="w-full" onClick={() => void doTransition('publie')}>
                      <Send className="h-4 w-4" aria-hidden="true" /> Publier
                    </Button>
                  )}

                  {canDelete && (
                    <Button type="button" variant="danger" size="sm" className="w-full" onClick={() => void doDelete()}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Annuler l'arrêté
                    </Button>
                  )}

                  {/* M17 (retour Manon) — envoi au demandeur et à l'annuaire,
                      UNIQUEMENT une fois signé (le serveur le refuse avant). */}
                  {(arrete.statut === 'signe' || arrete.statut === 'publie') && (
                    <Button type="button" variant="primary" size="sm" className="w-full"
                      onClick={() => setEnvoiOuvert(true)}>
                      <Send className="h-4 w-4" aria-hidden="true" /> Envoyer l'arrêté signé
                    </Button>
                  )}

                  {arrete.statut === 'annule' && (
                    <Alert variant="warning" icon={AlertCircle}>
                      Cet arrêté a été annulé.
                    </Alert>
                  )}
                  {arrete.statut === 'publie' && (
                    <Alert variant="info" icon={CheckCircle2}>
                      Arrêté publié le {new Date(arrete.publieAt!).toLocaleDateString('fr-FR')}.
                      Pour transmission au contrôle de légalité préfecture, utiliser Pastell (à intégrer).
                    </Alert>
                  )}
                </div>
              </Card>

              <RelectureValidationCard acteId={arrete.id} statut={arrete.statut}
                onEnvoyerSansCircuit={() => void doTransition('relecture_juridique')} onChange={() => void reload()} />

              {/* M19 (retour Manon) : joindre un document à l'arrêté — plan de
                  déviation, photo, courrier. PDF, Word ou image, 15 Mo max. */}
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-semibold">Pièces jointes</h2>
                <p className="mt-1 text-xs text-ink-muted">
                  Plan de déviation, photo, courrier… (PDF, Word ou image).
                </p>
                {pieces.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {pieces.map((pj) => (
                      <li key={pj.id} className="flex items-center gap-2 text-sm">
                        <button
                          type="button"
                          className="truncate text-left text-accent underline-offset-2 hover:underline"
                          title={`Ajoutée le ${new Date(pj.createdAt).toLocaleDateString('fr-FR')}${pj.ajouteParNom ? ` par ${pj.ajouteParNom}` : ''}`}
                          onClick={() => void ouvrirPiece(pj.id)}
                        >
                          {pj.nom}
                        </button>
                        <span className="shrink-0 text-xs text-ink-muted">{(pj.taille / 1024).toFixed(0)} Ko</span>
                        <button
                          type="button"
                          aria-label={`Retirer ${pj.nom}`}
                          className="ml-auto shrink-0 text-xs text-ink-muted hover:text-urgent"
                          onClick={() => void retirerPiece(pj.id)}
                        >
                          Retirer
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {pieces.length === 0 && (
                  <p className="mt-2 text-xs text-ink-muted">Aucune pièce pour l'instant.</p>
                )}
                <label className="mt-3 inline-block">
                  <span className="sr-only">Ajouter une pièce jointe</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    className="block w-full text-xs text-ink-muted file:mr-2 file:rounded-md file:border file:border-border file:bg-surface file:px-2 file:py-1 file:text-xs"
                    disabled={pieceEnvoi}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void ajouterPiece(f); e.target.value = ''; }}
                  />
                </label>
                {pieceEnvoi && <p className="mt-1 text-xs text-ink-muted">Envoi…</p>}
                {pieceErreur && <p className="mt-1 text-xs text-urgent">{pieceErreur}</p>}
              </Card>
            </aside>
          </div>
        )}
      </main>
      {envoiOuvert && (
        <EnvoiArreteSigneModal arreteId={arrete.id} arreteNumero={arrete.numero}
          onClose={() => setEnvoiOuvert(false)} />
      )}
      {dialogElement}{/* R13 — Dialog R2 (confirmations + prompt relecture) */}
    </div>
  );
}

// =============================================================================
// WORKFLOW CARD (4 étapes timeline)
// =============================================================================

function WorkflowCard({ arrete }: { arrete: Arrete }) {
  const currentIdx = STATUTS_ORDER.indexOf(arrete.statut as typeof STATUTS_ORDER[number]);

  function stepState(stepIdx: number): 'done' | 'current' | 'pending' {
    if (arrete.statut === 'annule') return 'pending';
    if (stepIdx < currentIdx) return 'done';
    if (stepIdx === currentIdx) return 'current';
    return 'pending';
  }

  const steps: Array<{ label: string; sub: string; date: string | null }> = [
    {
      label: 'Généré',
      sub: arrete.genereParFullName ? `Par ${arrete.genereParFullName}` : 'Auto depuis signalements',
      date: arrete.genereAt,
    },
    {
      label: 'Relecture juridique',
      sub: arrete.reluParFullName ? `Validée par ${arrete.reluParFullName}` : 'Secrétaire général',
      date: arrete.reluAt,
    },
    {
      label: 'Signature maire',
      sub: arrete.signeParFullName ? `Signé par ${arrete.signeParFullName}` : 'Signature électronique',
      date: arrete.signeAt,
    },
    {
      label: 'Publication',
      sub: arrete.publieAt ? 'Publié' : 'Via Pastell · contrôle légalité',
      date: arrete.publieAt,
    },
  ];

  return (
    <Card variant="glass" padded>
      <div className="flex items-center justify-between gap-2 mb-3">
        <SectionEyebrow as="div">Workflow validation</SectionEyebrow>
        <Badge variant={arrete.statut === 'annule' ? 'urgent' : currentIdx >= 3 ? 'success' : 'warning'} size="sm">
          {STATUTS_LABEL[arrete.statut]}
        </Badge>
      </div>
      {/* Frise PARTAGÉE (composant `ActeWorkflowFrise`) : la même présentation
          sert désormais le registre et la RH — la mairie l'a demandée partout.
          Elle était écrite en dur ici ; trois copies auraient divergé. */}
      <ActeWorkflowFrise
        statut={arrete.statut as StatutFrise}
        etapes={steps}
      />
    </Card>
  );
}

// =============================================================================
// FORMULAIRE ÉDITION
// =============================================================================

function ArreteEditForm({
  arrete,
  onCancel,
  onSaved,
}: {
  arrete: Arrete;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [titre, setTitre] = useState(arrete.titre);
  const [objet, setObjet] = useState(arrete.objet ?? '');
  // M14+M15 — voies et motifs multiples.
  const [voies, setVoies] = useState<Voie[]>(arrete.localisations ?? []);
  const [motifs, setMotifs] = useState<string[]>(arrete.motifs ?? []);
  const [type, setType] = useState(arrete.type);
  const [maireNom, setMaireNom] = useState(arrete.maireNom ?? '');
  const [considerants, setConsiderants] = useState((arrete.considerants ?? []).join('\n'));
  const [articlesText, setArticlesText] = useState(
    (arrete.articles ?? []).map((a) => `Article ${a.numero} — ${a.texte}`).join('\n\n')
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Parse "Article 1 — texte\n\nArticle 2 — texte" → [{numero:1, texte:'texte'}, ...]
  function parseArticles(s: string): Array<{ numero: number; texte: string }> {
    return s
      .split(/\n\n+/)
      .map((block) => block.trim())
      .filter(Boolean)
      .map((block, idx) => {
        const m = block.match(/^Article\s+(\d+)\s*[—–-]\s*(.+)$/s);
        if (m) return { numero: parseInt(m[1]!, 10), texte: m[2]!.trim() };
        return { numero: idx + 1, texte: block };
      });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await api.serviceTechnique.arretes.update(arrete.id, {
        titre: titre.trim(),
        objet: objet.trim() || null,
        ...nettoyerVoiesMotifs(voies, motifs),
        type,
        maireNom: maireNom.trim() || null,
        considerants: considerants.split('\n').map((s) => s.trim()).filter(Boolean),
        articles: parseArticles(articlesText),
      });
      await onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card variant="glass" padded>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
          Modifier l'arrêté {arrete.numero}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Type" htmlFor="type" required>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
            >
              <option value="voirie">Voirie / circulation</option>
              <option value="securite">Sécurité publique</option>
              <option value="urbanisme">Urbanisme</option>
              <option value="evenement">Événement</option>
              <option value="autre">Autre</option>
            </select>
          </Field>
          <Field label="Nom du maire" htmlFor="maire">
            <Input id="maire" value={maireNom} onChange={(e) => setMaireNom(e.target.value)} placeholder="Ex : Jean-Claude MARTIN" maxLength={150} />
          </Field>
        </div>

        <Field label="Titre" htmlFor="titre" required>
          <Input id="titre" value={titre} onChange={(e) => setTitre(e.target.value)} minLength={5} maxLength={300} required />
        </Field>

        <Field label="Objet" htmlFor="objet">
          <Input id="objet" value={objet} onChange={(e) => setObjet(e.target.value)} maxLength={500} placeholder="Ex : rue du Port — réfection de chaussée" />
        </Field>

        <VoiesMotifsEditor localisations={voies} motifs={motifs} onLocalisations={setVoies} onMotifs={setMotifs} />

        <Field label="Considérants (un par ligne)" htmlFor="cons">
          <textarea
            id="cons"
            value={considerants}
            onChange={(e) => setConsiderants(e.target.value)}
            rows={6}
            className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm font-mono text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
            placeholder="le Code général des collectivités territoriales&#10;le Code de la route&#10;la nécessité de procéder à des travaux"
          />
        </Field>

        <Field label="Articles (séparés par une ligne vide, format « Article N — texte »)" htmlFor="art">
          <textarea
            id="art"
            value={articlesText}
            onChange={(e) => setArticlesText(e.target.value)}
            rows={8}
            className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm font-mono text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
            placeholder={'Article 1 — La circulation sera interdite...\n\nArticle 2 — Une déviation...\n\nArticle 3 — Le présent arrêté...'}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel}>Annuler</Button>
          <Button type="submit" variant="primary" size="sm" disabled={submitting}>
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// =============================================================================
// PAGE CRÉATION (Nouvel arrêté, éventuellement pré-rempli depuis signalements)
// =============================================================================

export function ArreteNouveauPage() {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [params] = useSearchParams();
  const signalementIdsParam = params.get('signalementIds');

  const [type, setType] = useState('');
  // Refonte UX, chantier 5 — « Rédiger l'arrêté » depuis un palier de projet :
  // simple PRÉREMPLISSAGE (texte), l'agent relit et complète avant de créer.
  const [titre, setTitre] = useState(() => (params.get('titre') ?? '').slice(0, 300));
  const [objet, setObjet] = useState(() => (params.get('objet') ?? '').slice(0, 2000));
  // M14+M15 — voies et motifs multiples (préremplis depuis les signalements).
  const [voies, setVoies] = useState<Voie[]>([]);
  const [motifs, setMotifs] = useState<string[]>([]);
  const [considerants, setConsiderants] = useState('');
  const [articlesText, setArticlesText] = useState('');
  const [maireNom, setMaireNom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  // Le CATALOGUE COMMUN, filtré sur ce que la commune donne au service
  // technique (`?pour=service_technique`). Même mécanique que l'écran RH —
  // la seule différence entre les deux écrans est cette liste.
  const [modeles, setModeles] = useState<{ id: string; libelle: string; brique: string | null; categorie: string | null }[]>([]);
  const [modeleId, setModeleId] = useState('');
  const [corpsModele, setCorpsModele] = useState('');
  // Les trous du modèle, typés, et ce que le rédacteur y met.
  const [champs, setChamps] = useState<ChampModele[]>([]);
  const [valeursChamps, setValeursChamps] = useState<Record<string, string>>({});

  useEffect(() => {
    void (async () => {
      try { setModeles(await api.acteModeles.list({ pour: 'service_technique' })); }
      catch { setModeles([]); }
    })();
  }, []);

  /**
   * Les modèles du TYPE choisi.
   *
   * Le type du service technique (voirie, sécurité, urbanisme…) et la brique du
   * catalogue ne portent pas les mêmes mots : on rapproche sur le libellé, et à
   * défaut on propose tout plutôt que rien — mieux vaut une liste large qu'un
   * menu vide devant un agent qui doit rédiger.
   */
  /**
   * Les modèles de la catégorie choisie. Correspondance EXACTE — le type est
   * désormais la catégorie elle-même, plus une valeur figée qu'il fallait
   * rapprocher par mots-clés.
   */
  const modelesDuType = type ? modeles.filter((m) => m.categorie === type) : modeles;

  /** Choisir un modèle remplit le titre et le corps, variables déjà résolues. */
  async function choisirModele(id: string) {
    setModeleId(id);
    if (!id) return;
    try {
      const r = await api.acteModeles.preparer(id);
      setTitre(r.modele.libelle);
      const m = modeles.find((x) => x.id === id);
      if (m?.categorie) setType(m.categorie);
      // Le canevas est un DOCUMENT complet (visas → articles → recours → pied).
      // Il va dans `corps`, pas dans les considérants : sinon chaque ligne
      // recevait un « ; » et le générateur réimprimait ARRÊTE et le pied.
      setCorpsModele(r.corps);
      setChamps(r.champs);
      setValeursChamps({});
      setConsiderants('');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Modèle illisible');
    }
  }

  const signalementIds = useMemo(
    () => (signalementIdsParam ? signalementIdsParam.split(',') : []),
    [signalementIdsParam]
  );

  // Pré-remplissage si on arrive depuis un signalement
  useEffect(() => {
    async function loadDraft() {
      if (signalementIds.length === 0) return;
      setDraftLoading(true);
      try {
        const draft = await api.serviceTechnique.arretes.draftFromSignalements(signalementIds);
        setTitre(draft.titre);
        setObjet(draft.objet);
        setVoies(draft.localisations ?? []);
        setMotifs(draft.motifs ?? []);
        setConsiderants(draft.considerants.join('\n'));
        setArticlesText(draft.articles.map((a) => `Article ${a.numero} — ${a.texte}`).join('\n\n'));
      } catch (err) {
        setError(err instanceof ApiException ? err.payload.error : 'Erreur chargement brouillon');
      } finally {
        setDraftLoading(false);
      }
    }
    void loadDraft();
  }, [signalementIds]);

  function parseArticles(s: string): Array<{ numero: number; texte: string }> {
    return s.split(/\n\n+/).map((b) => b.trim()).filter(Boolean).map((block, idx) => {
      const m = block.match(/^Article\s+(\d+)\s*[—–-]\s*(.+)$/s);
      if (m) return { numero: parseInt(m[1]!, 10), texte: m[2]!.trim() };
      return { numero: idx + 1, texte: block };
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const res = await api.serviceTechnique.arretes.create({
        // Le modèle choisi : son circuit de validation s'applique à l'arrêté (28/09).
        modeleId: modeleId || undefined,
        // Le serveur exige un type non vide : « tous » vaut « autre ».
        type: type || 'autre',
        titre: titre.trim(),
        objet: objet.trim() || undefined,
        ...nettoyerVoiesMotifs(voies, motifs),
        considerants: considerants.split('\n').map((s) => s.trim()).filter(Boolean),
        articles: parseArticles(articlesText),
        // Le canevas, avec les champs saisis substitués. Ceux laissés vides
        // gardent leurs accolades — un trou doit rester visible.
        corps: corpsModele.trim()
          ? appliquerChamps(corpsModele, valeursChamps)
          : undefined,
        maireNom: maireNom.trim() || undefined,
        signalementIds: signalementIds.length > 0 ? signalementIds : undefined,
      });
      navigate(`/service-technique/arretes/${res.id}`);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur création');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen text-ink">
      {/* Topbar glass */}
      <AppHeader module="Service technique" accent="tech" />

      {/* Sub-nav : retour */}
      <div
        className="border-b border-border backdrop-blur-glass px-4 py-2"
        style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
      >
        <div className="mx-auto max-w-4xl">
          <button
            type="button"
            onClick={() => navigate('/service-technique/arretes')}
            className="inline-flex items-center gap-1.5 rounded-r-md text-xs text-ink-muted transition-colors hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg px-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Liste des arrêtés
          </button>
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-5 animate-fade-in">
        {/* En-tête */}
        <div className="mb-4 flex items-center gap-3">
          <LogoMark icon={FileSpreadsheet} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">Arrêté municipal</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Nouvel arrêté
            </h1>
          </div>
        </div>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}
        {draftLoading && (
          <Alert variant="info" icon={Clock} className="mb-3">
            Chargement du brouillon depuis les signalements…
          </Alert>
        )}
        {signalementIds.length > 0 && !draftLoading && (
          <Alert variant="info" icon={CheckCircle2} className="mb-3">
            Pré-rempli automatiquement depuis {signalementIds.length} signalement{signalementIds.length > 1 ? 's' : ''}.
            Vérifiez les considérants et articles avant de créer.
          </Alert>
        )}

        <Card variant="glass" padded>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Type d'arrêté" htmlFor="type"
                hint="Restreint la liste des modèles proposés">
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                >
                  <option value="">Tous les types</option>
                  {/* Les catégories du CATALOGUE ouvert au service technique.
                      Décocher un domaine depuis la RH les fait disparaître
                      d'ici — ce que la liste figée d'avant ne permettait pas. */}
                  {[...new Set(modeles.map((m) => m.categorie).filter(Boolean) as string[])]
                    .sort().map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              {/* Le TYPE d'abord, le MODÈLE ensuite et filtré par lui : sur 58
                  modèles, proposer une liste que le type ne restreint pas mène
                  à choisir un modèle de voirie sous un type « urbanisme ». */}
              <Field label="Modèle" htmlFor="modele"
                hint={modelesDuType.length > 0
                  ? `${modelesDuType.length} modèles pour ce type`
                  : 'Aucun modèle pour ce type — saisie libre'}>
                <select
                  id="modele" value={modeleId}
                  onChange={(e) => void choisirModele(e.target.value)}
                  className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                >
                  <option value="">— Sans modèle (saisie libre) —</option>
                  {[...new Set(modelesDuType.map((m) => m.categorie ?? 'Autres'))].sort().map((c) => (
                    <optgroup key={c} label={c}>
                      {modelesDuType.filter((m) => (m.categorie ?? 'Autres') === c).map((m) => (
                        <option key={m.id} value={m.id}>{m.libelle}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="Nom du maire" htmlFor="maire">
                <Input id="maire" value={maireNom} onChange={(e) => setMaireNom(e.target.value)} placeholder="Ex : Jean-Claude MARTIN" maxLength={150} />
              </Field>
            </div>

            <Field label="Titre" htmlFor="titre" required>
              <Input id="titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex : Portant réglementation temporaire de la circulation" minLength={5} maxLength={300} required />
            </Field>

            <Field label="Objet" htmlFor="objet">
              <Input id="objet" value={objet} onChange={(e) => setObjet(e.target.value)} maxLength={500} placeholder="Ex : rue du Port — réfection de chaussée" />
            </Field>

            <VoiesMotifsEditor localisations={voies} motifs={motifs} onLocalisations={setVoies} onMotifs={setMotifs} />

            {/* Un champ par variable non résolue du modèle. */}
            <ChampsModele
              champs={champs}
              valeurs={valeursChamps}
              onChange={(cle, v) => setValeursChamps((prev) => ({ ...prev, [cle]: v }))}
            />

            {/* Le texte du modèle. Il était chargé et envoyé, mais AFFICHÉ NULLE
                PART : le formulaire ne montrait que les champs structurés, si
                bien que choisir un modèle ne remplissait visiblement rien. */}
            {corpsModele ? (
              <Field label="Texte de l'arrêté" htmlFor="corps-modele"
                hint="Repris du modèle, variables déjà résolues — modifiable">
                <textarea
                  id="corps-modele"
                  value={corpsModele}
                  onChange={(e) => setCorpsModele(e.target.value)}
                  rows={18}
                  className="w-full rounded-r-lg border border-border bg-surface/60 p-3 font-mono text-sm text-ink focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
                />
              </Field>
            ) : (
            <>
            <Field label="Considérants (un par ligne)" htmlFor="cons">
              <textarea
                id="cons"
                value={considerants}
                onChange={(e) => setConsiderants(e.target.value)}
                rows={6}
                className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm font-mono text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                placeholder="le Code général des collectivités territoriales&#10;le Code de la route&#10;la nécessité de procéder à des travaux"
              />
            </Field>

            <Field label="Articles (séparés par une ligne vide, format « Article N — texte »)" htmlFor="art">
              <textarea
                id="art"
                value={articlesText}
                onChange={(e) => setArticlesText(e.target.value)}
                rows={8}
                className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm font-mono text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              />
            </Field>
            </>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/service-technique/arretes')}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                {submitting ? 'Création…' : 'Créer l\'arrêté (brouillon)'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
